import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import RefreshIcon from "@mui/icons-material/Refresh";

import { PERMISSIONS } from "../../constants";
import ChangeEmailDialog from "./ChangeEmailDialog";
import DialogConfirmation from "../generic/DialogConfirmation";
import {
  inviteEntityMember,
  listEntityMembers,
  memberErrorLabel,
  resendEntityInvite,
  revokeEntityInvite,
  revokeEntityMember,
  setEntityMemberRole,
} from "../../services/backend/EntityService";

// The company's staff: who has an account and which invitations are live.
//
// An administrator invites by email; the invitee confirms that address with a
// code and verifies their mobile, and only then does their account exist. No
// account is created directly from here, on purpose: nobody can grant access to
// an address without the owner of that address accepting.

const ROLE_LABELS = {
  ENTITY_ADMIN: "Administrador",
  ENTITY_MEMBER: "Personal",
};

const ROLE_HELP = {
  ENTITY_ADMIN: "Puede editar los datos de la empresa e invitar a más personas.",
  ENTITY_MEMBER: "Puede consultar la información y cargar documentos.",
};

const fmtDate = (value) => {
  if (!value) return "—";
  const raw = value.toDate ? value.toDate() : new Date(value.seconds ? value.seconds * 1000 : value);
  if (Number.isNaN(raw.getTime())) return "—";
  return raw.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
};

export default function MyEntityMembers({ nit }) {
  const user = useSelector((state) => state.user.value);
  const perms = user?.permissions || [];
  const canInvite = perms.includes(PERMISSIONS.MY_ENTITY.INVITE_MEMBER);
  const canManage = perms.includes(PERMISSIONS.MY_ENTITY.MANAGE_MEMBERS);

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("ENTITY_MEMBER");
  const [confirm, setConfirm] = useState(null); // { kind, id, label }

  const load = useCallback(async () => {
    setLoading(true);
    const result = await listEntityMembers(nit, user);
    setLoading(false);
    if (!result.ok) {
      setError(memberErrorLabel(result.error));
      return;
    }
    setMembers(result.data?.members || []);
    setInvites(result.data?.invites || []);
  }, [nit, user]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, successMessage) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    const result = await fn();
    setBusy(false);
    if (!result.ok) {
      setError(memberErrorLabel(result.error));
      return false;
    }
    if (successMessage) setNotice(successMessage);
    await load();
    return true;
  };

  const submitInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    const ok = await run(
      () => inviteEntityMember({ nit, email, role: inviteRole }, user),
      `Invitación enviada a ${email}.`
    );
    if (ok) {
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("ENTITY_MEMBER");
    }
  };

  const activeMembers = members.filter((m) => !m.disabled);
  const revokedMembers = members.filter((m) => m.disabled);

  return (
    <Box>
      <ChangeEmailDialog
        open={emailOpen}
        onClose={() => { setEmailOpen(false); load(); }}
      />
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">Personal</Typography>
          <Typography variant="body2" color="text.secondary">
            Las personas de tu empresa que pueden entrar a la plataforma.
          </Typography>
        </Box>
        {canInvite && (
          <Button
            variant="contained"
            startIcon={<PersonAddAltIcon />}
            onClick={() => setInviteOpen(true)}
            disabled={busy}
            sx={{ textTransform: "none" }}
          >
            Invitar
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {loading ? (
        <Typography variant="body2" color="text.secondary">Cargando…</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Persona</TableCell>
              <TableCell>Correo</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {activeMembers.map((m) => {
              const isSelf = m.userId === user?.userId;
              const role = m.isAdmin ? "ENTITY_ADMIN" : "ENTITY_MEMBER";
              return (
                <TableRow key={m.userId}>
                  <TableCell>
                    {[m.name, m.lastName].filter(Boolean).join(" ") || "—"}
                    {isSelf && <Chip size="small" label="Tú" sx={{ ml: 1 }} />}
                    {m.position && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {m.position}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {m.email || "—"}
                    {/* El correo es la credencial: no hay contraseña, se entra
                        con un código que llega ahí. Cambiarlo toca Firebase Auth
                        además de la ficha, así que sólo lo puede hacer su
                        dueño y por su propio flujo. */}
                    {isSelf && (
                      <Button
                        size="small" disabled={busy}
                        onClick={() => setEmailOpen(true)}
                        sx={{ textTransform: "none", ml: 1 }}
                      >
                        Cambiar
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    {canManage && !isSelf ? (
                      <TextField
                        select size="small" value={role} disabled={busy}
                        onChange={(e) => run(
                          () => setEntityMemberRole({ nit, userId: m.userId, role: e.target.value }, user),
                          "Rol actualizado."
                        )}
                        sx={{ minWidth: 160 }}
                      >
                        {Object.keys(ROLE_LABELS).map((key) => (
                          <MenuItem key={key} value={key}>{ROLE_LABELS[key]}</MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <Chip size="small" label={ROLE_LABELS[role]} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canManage && !isSelf && (
                      <Tooltip title="Retirar acceso">
                        <span>
                          <IconButton
                            size="small" disabled={busy}
                            onClick={() => setConfirm({
                              kind: "member", id: m.userId,
                              label: m.email || m.userId,
                            })}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {activeMembers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    Todavía no hay nadie más. Invita a las personas de tu equipo.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {invites.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Invitaciones pendientes</Typography>
          <Table size="small">
            <TableBody>
              {invites.map((i) => (
                <TableRow key={i.inviteId}>
                  <TableCell>{i.email}</TableCell>
                  <TableCell>
                    <Chip size="small" label={ROLE_LABELS[i.invitedRole] || i.invitedRole} />
                  </TableCell>
                  <TableCell>
                    {i.status === "EXPIRED" ? (
                      <Chip size="small" color="warning" label="Vencida" />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Vence el {fmtDate(i.expiresAt)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canInvite && (
                      <>
                        <Tooltip title="Reenviar">
                          <span>
                            <IconButton
                              size="small" disabled={busy}
                              onClick={() => run(
                                () => resendEntityInvite({ nit, inviteId: i.inviteId }, user),
                                `Invitación reenviada a ${i.email}.`
                              )}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancelar invitación">
                          <span>
                            <IconButton
                              size="small" disabled={busy}
                              onClick={() => setConfirm({
                                kind: "invite", id: i.inviteId, label: i.email,
                              })}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {revokedMembers.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Accesos retirados</Typography>
          <Stack spacing={0.5}>
            {revokedMembers.map((m) => (
              <Typography key={m.userId} variant="body2" color="text.secondary">
                {m.email} — sin acceso
              </Typography>
            ))}
          </Stack>
        </>
      )}

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invitar a alguien de tu equipo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Le llegará un correo con un enlace. Para entrar tendrá que confirmar
            ese correo y verificar su celular.
          </Typography>
          <TextField
            autoFocus fullWidth margin="dense" type="email"
            label="Correo electrónico"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <TextField
            select fullWidth margin="dense" label="Rol"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            helperText={ROLE_HELP[inviteRole]}
          >
            {Object.keys(ROLE_LABELS).map((key) => (
              <MenuItem key={key} value={key}>{ROLE_LABELS[key]}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button
            variant="contained" onClick={submitInvite}
            disabled={busy || !inviteEmail.trim()}
            sx={{ textTransform: "none" }}
          >
            Enviar invitación
          </Button>
        </DialogActions>
      </Dialog>

      <DialogConfirmation
        confirmInfo={{
          open: !!confirm,
          title: confirm?.kind === "invite" ? "Cancelar invitación" : "Retirar acceso",
          confirm: confirm?.kind === "invite" ? "Cancelar invitación" : "Retirar",
        }}
        content={
          confirm?.kind === "invite"
            ? `La invitación de ${confirm?.label} dejará de servir.`
            : `${confirm?.label} dejará de tener acceso a la plataforma. Su historial se conserva.`
        }
        handleCloseDialog={async (accepted) => {
          const target = confirm;
          setConfirm(null);
          if (!accepted || !target) return;
          if (target.kind === "invite") {
            await run(() => revokeEntityInvite({ nit, inviteId: target.id }, user), "Invitación cancelada.");
          } else {
            await run(() => revokeEntityMember({ nit, userId: target.id }, user), "Acceso retirado.");
          }
        }}
      />
    </Box>
  );
}
